import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@chainlit/react-client';

import LanguageSelector from '@/components/LanguageSelector';
import {
  getStoredAppLanguage,
  storeAppLanguage,
  type AppLanguage
} from '@/lib/language';

export default function LanguageSwitcher() {
  const [languageInUse, setLanguageInUse] = useState(getStoredAppLanguage);
  const { i18n } = useTranslation();
  const { user } = useAuth();

  function selectLanguage(language: AppLanguage) {
    storeAppLanguage(language);
    setLanguageInUse(language);
  }

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    async function applyLanguage() {
      try {
        const response = await fetch(
          `/project/translations?language=${encodeURIComponent(languageInUse)}`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error('Unable to load translations');
        const translations = await response.json();
        if (!active) return;
        i18n.addResourceBundle(
          languageInUse,
          'translation',
          translations.translation,
          true,
          true
        );
      } catch {
        if (controller.signal.aborted) return;
      }

      if (active) await i18n.changeLanguage(languageInUse);
    }

    void applyLanguage();
    return () => {
      active = false;
      controller.abort();
    };
  }, [i18n, languageInUse]);

  useEffect(() => {
    if (!user?.identifier) return;
    fetch('/api/set-language', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ language: languageInUse })
    }).catch(() => undefined);
  }, [languageInUse, user?.identifier]);

  return (
    <LanguageSelector
      id="language-switcher-button"
      value={languageInUse}
      onChange={selectLanguage}
      className="fongpt-header-control fongpt-sidebar-nav-item border-0 bg-transparent shadow-none"
    />
  );
}
